import { defineStore } from "pinia";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import {
  AllowBlockListType,
  AllowListSubType,
  EmailSecurityAllowBlockItemType,
  PhishingAction,
} from "@/constants/phishing";
import { downloadFile } from "@/_helpers/download";
import type { Paged, Pagination } from "@/types";
import { FilterContext, useFiltersStore } from "@/_store/filters.module";
import type { EmailError } from "@/constants/emails";
import type {
  ValidateAllowBlockTermsPayload,
  ValidateAllowBlockTermsResponse,
  TldTermsResponse,
} from "@/_store/email-security/email-settings.module.ts";
import { StoreModule } from "@/constants/store.ts";

export interface GlobalAllowBlockListItem {
  name: string;
  type: EmailSecurityAllowBlockItemType;
  allowList: boolean;
  senderClassificationType: AllowListSubType;
  updateTimestamp: number;
  emailsProcessedCount: number;
}

export interface SortItem {
  key: string;
  order?: boolean | "asc" | "desc";
}

export interface GlobalAllowBlockListState {
  allowBlockList: Paged<GlobalAllowBlockListItem>;
  pagination: Pagination;
  loading: boolean;
  sortingOptions?: string;
}

export interface RemoveFromGlobalAllowBlockListPayload {
  name: string;
  listType: AllowBlockListType;
  senderClassificationType: AllowListSubType;
}

export interface GlobalAllowBlockListForm {
  items: string[];
  errors: EmailError[];
  action: PhishingAction;
}

export const defaultGlobalAllowBlockListState: GlobalAllowBlockListState = {
  allowBlockList: {
    items: [],
    total: 0,
  },
  pagination: {
    page: 0,
    pageSize: 25,
  },
  sortingOptions: undefined,
  loading: false,
};

export const useGlobalAllowBlockListStore = defineStore(StoreModule.GLOBAL_ALLOW_BLOCK_LIST, {
  state: (): GlobalAllowBlockListState => ({ ...defaultGlobalAllowBlockListState }),
  actions: {
    setPagination(pagination: Pagination) {
      this.pagination = pagination;
    },
    async setSortingOptions(sortingOptions: SortItem[]) {
      this.sortingOptions = sortingOptions[0]
        ? `${sortingOptions[0].key},${sortingOptions[0].order}`
        : undefined;
    },
    resetPagination() {
      this.pagination = { ...defaultGlobalAllowBlockListState.pagination };
    },
    async getGlobalAllowBlockList() {
      this.loading = true;

      const filtersStore = useFiltersStore();
      const filters = filtersStore.filters[FilterContext.GLOBAL_ALLOW_BLOCK_LIST];

      const request = {
        ...api.getGlobalAllowBlockList(),
        params: {
          ...filters,
          ...this.pagination,
          sort: this.sortingOptions,
        },
      };

      try {
        const { data } = await axiosInstance.request<Paged<GlobalAllowBlockListItem>>(request);
        this.allowBlockList = data;
      } catch (error) {
        console.error("Failed to fetch global allow/block list:", error);
      } finally {
        this.loading = false;
      }
    },
    async removeFromAllowlist(payload: GlobalAllowBlockListItem) {
      await this.removeFromAllowBlockList({
        name: payload.name,
        listType: AllowBlockListType.ALLOW,
        senderClassificationType: payload.senderClassificationType,
      });
    },
    async removeFromBlocklist(payload: GlobalAllowBlockListItem) {
      await this.removeFromAllowBlockList({
        name: payload.name,
        listType: AllowBlockListType.BLOCK,
        senderClassificationType: payload.senderClassificationType,
      });
    },
    async addToGlobalAllowBlockList(payload: GlobalAllowBlockListForm) {
      const { items, action } = payload;
      const data = {
        items,
        allowList: action === PhishingAction.ADD_TO_ALLOWLIST,
      };

      const request = {
        ...api.getGlobalAllowBlockList(),
        data,
        method: "POST",
      };

      try {
        await axiosInstance.request(request);
        await this.getGlobalAllowBlockList();
      } catch (error) {
        console.error("Failed to add to global allow/block list:", error);
        throw error;
      }
    },
    async validateAllowBlockTerms(
      payload: ValidateAllowBlockTermsPayload
    ): Promise<ValidateAllowBlockTermsResponse> {
      const request = {
        ...api.validateGlobalAllowBlockList(),
        data: payload,
      };

      const { data } = await axiosInstance.request<ValidateAllowBlockTermsResponse>(request);

      return data;
    },
    async getExistingTlds(): Promise<string[] | null> {
      try {
        const { data } = await axiosInstance.request<TldTermsResponse>({
          ...api.getGlobalAllowBlockListExistingTlds(),
        });

        return data?.terms ?? [];
      } catch (error) {
        console.error("Failed to fetch existing TLDs:", error);

        return null;
      }
    },
    async getRestrictedTlds(): Promise<string[] | null> {
      try {
        const { data } = await axiosInstance.request<TldTermsResponse>({
          ...api.getGlobalAllowBlockListRestrictedTlds(),
        });

        return data?.terms ?? [];
      } catch (error) {
        console.error("Failed to fetch restricted TLDs:", error);

        return null;
      }
    },
    async removeFromAllowBlockList(payload: RemoveFromGlobalAllowBlockListPayload) {
      const request = {
        ...api.getGlobalAllowBlockList(),
        method: "DELETE",
        params: {
          name: payload.name,
          listType: payload.listType,
          senderClassificationType: payload.senderClassificationType,
        },
      };

      try {
        await axiosInstance.request(request);
        await this.getGlobalAllowBlockList();
      } catch (error) {
        console.error("Failed to remove from global allow/block list:", error);
        throw error;
      }
    },
    async importGlobalAllowBlockListFromCsv(file: File) {
      const data = new FormData();
      data.append("file", file);

      const request = {
        ...api.importGlobalAllowBlockList(),
        data,
      };

      try {
        return await axiosInstance.request(request);
      } catch (error) {
        console.error("Failed to import global allow/block list from CSV:", error);
        throw error;
      }
    },
    async downloadGlobalAllowBlockListExampleCsv() {
      const request = {
        ...api.getGlobalAllowBlockListExampleCsv(),
      };

      try {
        const { data, headers } = await axiosInstance.request(request);
        const blob = new Blob([data], { type: headers["content-type"] as string | undefined });
        const fileName = "GlobalAllowBlockListExample.csv";

        downloadFile(blob, fileName);
      } catch (error) {
        console.error("Failed to download global allow/block list example CSV:", error);
      }
    },
  },
});
